import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { IssueCustomerMessage } from '../model/issue-customer-message.model';
import { IssueFilterModel } from '../model/issue-filter.model';
import { IssueSelfHelpModel } from '../model/issue-selfhelp.model';
import { IssueModel } from '../model/issue.model';

@Injectable()
export class IssueService {
  protected issueURL = RestApiModel.STARGATE_API_URL + '/issues';
  protected editIssueURL = RestApiModel.PROXY_API_URL + '/issue';
  protected customerMessageURL = RestApiModel.PROXY_API_URL + '/messages';
  protected customerMessage = RestApiModel.PROXY_API_URL + '/message';
  private issueId: string;
  protected shortDescriptionURL =
    RestApiModel.PROXY_API_URL + '/shortDescriptions';
  protected customerURL = RestApiModel.PROXY_API_URL + '/customers';
  protected siteURL = RestApiModel.PROXY_API_URL + '/sites';
  protected selfHelpURL = RestApiModel.STARGATE_API_URL + '/issue';
  protected selfHelf = RestApiModel.FORMS_API_URL + '/upsertForm';
  protected siteMessagesURL = RestApiModel.STARGATE_API_URL;

  constructor(private http: HttpClient) {}
  /**
   * Get All Short Description.
   * @returns {Observable<any>}
   */
  getAllShortDescription(lineOfServiceId: string): Observable<any> {
    let Url: any;
    if (!lineOfServiceId) {
      Url = this.shortDescriptionURL + '?';
    } else {
      Url = this.shortDescriptionURL + '?lineOfServiceId=' + lineOfServiceId;
    }
    return this.http.get(`${Url}`).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get Customer list.
   * @returns {Observable<any>}
   */
  getCustomer(): Observable<any> {
    return this.http.get(this.customerURL).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get all Sites available for this customer
   * @returns {Observable<any>}
   */
  getAllSitesForCustomer(customerNumber: string): Observable<any> {
    return this.http
      .get(this.siteURL + `?customerNumber=${customerNumber}&pageSize=1000`)
      .pipe(
        map((res: any) => res),
        catchError(error => Observable.of(error))
      );
  }

  /**
   * Get All Issue with parameters
   * for All query=*
   * for selected loc pass los id like ?query=losid
   * @param name
   */
  getIssues(query: IssueFilterModel): Observable<Array<IssueModel>> {
    let Url: any;

    if (query.customerNumber && query.siteId && query.quickSearch) {
      Url =
        this.issueURL +
        '?customerNumber=' +
        query.customerNumber +
        '&query=' +
        query.quickSearch +
        '&siteId=' +
        query.siteId;
    } else if (query.customerNumber && query.quickSearch) {
      Url =
        this.issueURL +
        '?customerNumber=' +
        query.customerNumber +
        '&query=' +
        query.quickSearch;
    } else if (query.customerNumber && query.siteId) {
      Url =
        this.issueURL +
        '?customerNumber=' +
        query.customerNumber +
        '&siteId=' +
        query.siteId;
    } else if (query.customerNumber) {
      Url = this.issueURL + '?customerNumber=' + query.customerNumber;
    } else if (query.quickSearch) {
      Url = this.issueURL + '?query=' + query.quickSearch;
    } else {
      Url = this.issueURL + '?query=';
    }
    return this.http.get(`${Url}`).pipe(
      map((res: any) => {
        return res ? res : [];
      }),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * PUT - Updates an issue.
   */
  updateIssue(issueModel: IssueModel): Observable<any> {
    const payload = { ...issueModel };
    return this.http
      .put(this.editIssueURL + '/' + issueModel.id, payload as any)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError(error => Observable.of(error))
      );
  }

  /**
   * Remove duplicate objects from array of objects.
   */
  removeDuplicate(arr, comp) {
    const unique = arr
      .map(e => e[comp])
      // store the keys of the unique objects
      .map((e, i, final) => final.indexOf(e) === i && i)
      // eliminate the dead keys & store unique objects
      .filter(e => arr[e])
      .map(e => arr[e]);
    return unique;
  }

  /**
   * Get Self Help Details.
   * @returns {Observable<any>}
   */
  getSelfHelpDetail(issueId: String): Observable<IssueSelfHelpModel> {
    let Url: any;
    Url = this.selfHelpURL + '/' + issueId + '/selfHelp';
    return this.http.get(`${Url}`).pipe(
      map((res: any) => {
        res.elements = res.items;
        res.elements.forEach(element => {
          element.text = element.text === '' ? 'Not Set' : element.text;
        });
        delete res.items;
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }
  /**
   * PUT update a selfhelp items
   */
  updateSelfHelp(issueSelfHelpModel: IssueSelfHelpModel): Observable<any> {
    let payload = {
      ...issueSelfHelpModel
    };
    payload.elementType = 'Page';
    payload.type = 'Self Help';
    return this.http.post(this.selfHelf, payload).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }

  getCustomerMessageDetail(
    issueId: String,
    customerNumber: String
  ): Observable<any> {
    let Url: any;
    Url =
      this.customerMessageURL +
      '?issueIds=' +
      issueId +
      '&customerNumber=' +
      customerNumber;
    return this.http.get(`${Url}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }
  getsiteSpecificMessageDetail(
    issueId: String,
    customerNumber: String,
    siteId: String
  ): Observable<any> {
    let Url: any;
    Url =
      this.customerMessageURL +
      '?issueIds=' +
      issueId +
      '&customerNumber=' +
      customerNumber +
      '&siteId=' +
      siteId;
    return this.http.get(`${Url}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }

  putMessage(customerMessage: IssueCustomerMessage) {
    let payload = {
      ...customerMessage
    };
    if (payload.startDate === 'Not Set' || payload.startDate === null) {
      payload.startDate = null;
    } else {
      payload.startDate = new Date(payload.startDate);
    }
    if (payload.endDate === 'Not Set' || payload.endDate === null) {
      payload.endDate = null;
    } else {
      payload.endDate = new Date(payload.endDate);
    }
    delete payload.links;
    delete payload.id;
    let url = this.customerMessage + '/' + customerMessage.id;
    return this.http.put(url, payload).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }

  postMessage(customerMessage: IssueCustomerMessage) {
    let payload = {
      ...customerMessage
    };
    if (payload.startDate === 'Not Set' || payload.startDate === null) {
      delete payload.startDate;
    } else {
      payload.startDate = new Date(payload.startDate);
    }
    if (payload.endDate === 'Not Set' || payload.endDate === null) {
      delete payload.endDate;
    } else {
      payload.endDate = new Date(payload.endDate);
    }
    delete payload.links;
    let url = this.customerMessage;
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * GET refresh Issue metadata(booleans) for selfhelp/questions
   */
  refreshIssueMetaData(issueId): Observable<any> {
    let Url = RestApiModel.STARGATE_API_URL + `/issue/${issueId}/refresh`;
    return this.http.get(Url).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Method to get messages for an Issue
   */
  getSiteMessages(issueId: String, customerNumber: string): Observable<any> {
    let Url: any;
    Url =
      this.siteMessagesURL +
      `/messages/${issueId}/sites?customerNumber=${customerNumber}`;
    return this.http.get(Url).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(error => Observable.of(error))
    );
  }
}
